import { useState, type ReactNode } from 'react';
import {
  useTweaks,
  type CardVariant,
  type StatLayout,
  type Arena,
  type ThemeIntensity,
  type AnimIntensity,
} from './useTweaks.js';

const CARD_VARIANTS: Array<[CardVariant, string]> = [
  ['classic', 'Classic Topps'],
  ['holo', 'Holographic'],
  ['brutal', 'Stencil / Brutal'],
  ['neon', 'Neon Arena'],
];
const STAT_LAYOUTS: StatLayout[] = ['table', 'bars', 'radial'];
const ARENAS: Array<[Arena, string]> = [
  ['stadium', 'Stadium Lights'],
  ['ring', 'In The Ring'],
  ['smoke', 'Smoke & Fire'],
  ['pyro', 'Pyrotechnics'],
];
const THEMES: Array<[ThemeIntensity, string]> = [
  ['balanced', 'Balanced (red+blue)'],
  ['redHeavy', 'Red dominant'],
  ['blueHeavy', 'Blue dominant'],
  ['monochrome', 'Monochrome black'],
];
const ANIMS: AnimIntensity[] = ['subtle', 'cinematic', 'overTop'];

export function TweaksPanel() {
  const t = useTweaks();
  const [open, setOpen] = useState(false);
  return (
    <div className={`tweaks ${open ? 'open' : ''}`}>
      <button
        type="button"
        className="tweaks-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-label="Settings"
      >
        ⚙
      </button>
      {open && (
        <div className="tweaks-body">
          <Section label="Card Style">
            <Select
              label="Variant"
              value={t.cardVariant}
              options={CARD_VARIANTS}
              onChange={(v) => t.set('cardVariant', v)}
            />
            <Radio
              label="Stats"
              value={t.statLayout}
              options={STAT_LAYOUTS.map((v) => [v, v]) as Array<[StatLayout, string]>}
              onChange={(v) => t.set('statLayout', v)}
            />
          </Section>
          <Section label="Arena">
            <Select
              label="Background"
              value={t.arena}
              options={ARENAS}
              onChange={(v) => t.set('arena', v)}
            />
          </Section>
          <Section label="Theme">
            <Select
              label="Color intensity"
              value={t.themeIntensity}
              options={THEMES}
              onChange={(v) => t.set('themeIntensity', v)}
            />
          </Section>
          <Section label="Motion">
            <Radio
              label="Animation"
              value={t.animIntensity}
              options={ANIMS.map((v) => [v, v]) as Array<[AnimIntensity, string]>}
              onChange={(v) => t.set('animIntensity', v)}
            />
          </Section>
          <Section label="Haptics">
            <button
              type="button"
              className="tweaks-toggle-row"
              onClick={() => t.set('haptics', !t.haptics)}
            >
              {t.haptics ? 'ON' : 'OFF'}
            </button>
          </Section>
        </div>
      )}
    </div>
  );
}

function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="tweaks-section">
      <div className="tweaks-section-label">{label}</div>
      {children}
    </div>
  );
}

interface SelectProps<T extends string> {
  label: string;
  value: T;
  options: Array<[T, string]>;
  onChange: (v: T) => void;
}

function Select<T extends string>({ label, value, options, onChange }: SelectProps<T>) {
  return (
    <label className="tweaks-row">
      <span>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value as T)}>
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </label>
  );
}

interface RadioProps<T extends string> {
  label: string;
  value: T;
  options: Array<[T, string]>;
  onChange: (v: T) => void;
}

function Radio<T extends string>({ label, value, options, onChange }: RadioProps<T>) {
  return (
    <div className="tweaks-row">
      <span>{label}</span>
      <div className="tweaks-radio">
        {options.map(([v, l]) => (
          <button
            key={v}
            type="button"
            className={value === v ? 'on' : ''}
            onClick={() => onChange(v)}
          >
            {l}
          </button>
        ))}
      </div>
    </div>
  );
}
